import { useState } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Trash2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function AccountSettings({ user, onLogout }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SIL') {
      toast.error('Lütfen "SIL" yazın');
      return;
    }

    setDeleting(true);
    try {
      await axiosInstance.delete('/auth/account');
      toast.success('Hesabınız başarıyla silindi');
      setTimeout(() => {
        onLogout();
        navigate('/login');
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hesap silinirken hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Hesap Ayarları
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Hesap bilgilerinizi yönetin</p>
      </div>

      <div className="max-w-3xl space-y-6">
        {/* Hesap Bilgileri */}
        <Card className="shadow-lg border-0 rounded-2xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardTitle className="text-lg sm:text-xl flex items-center">
              <User className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Hesap Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-2xl">
                <p className="text-sm text-gray-600 mb-1">Kullanıcı Adı</p>
                <p className="text-lg font-semibold text-gray-900">{user?.username}</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-2xl">
                <p className="text-sm text-gray-600 mb-1">E-posta</p>
                <p className="text-lg font-semibold text-gray-900">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tehlikeli Bölge */}
        <Card className="shadow-lg border-2 border-red-200 rounded-2xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-red-500 to-pink-500 text-white">
            <CardTitle className="text-lg sm:text-xl flex items-center">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Tehlikeli Bölge
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {!showDeleteConfirm ? (
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl">
                  <p className="text-sm text-red-700">
                    <strong>Uyarı:</strong> Hesabınızı sildiğinizde tüm verileriniz (mağazalar, markalar, kategoriler, ürünler) kalıcı olarak silinecektir. Bu işlem geri alınamaz!
                  </p>
                </div>
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  variant="destructive"
                  className="w-full bg-red-500 hover:bg-red-600 rounded-2xl"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Hesabı Sil
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl">
                  <p className="text-sm text-red-700 font-semibold mb-2">
                    Hesabınızı silmek üzeresiniz!
                  </p>
                  <p className="text-sm text-red-600">
                    Devam etmek için aşağıdaki kutucuğa <strong>"SIL"</strong> yazın (tırnak işareti olmadan):
                  </p>
                </div>
                
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="SIL yazın"
                  className="w-full px-4 py-3 border-2 border-red-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500"
                />

                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    variant="outline"
                    className="flex-1 rounded-2xl"
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'SIL' || deleting}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-2xl"
                  >
                    {deleting ? 'Siliniyor...' : 'Hesabı Kalıcı Olarak Sil'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
